import { SelectOptionType, addToast } from '@octano/global-ui';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Col, Row } from 'reactstrap';

import { downloadFromBlob } from '../../../../utils/blob';
import {
  downloadAgreedPaymentsByStudent,
  listAgreedPaymentsByStudent,
} from '../../../FinancialPortal/AgreedPayments/api';
import { AgreedPaymentsTable } from '../../../FinancialPortal/AgreedPayments/parts/PaymentsTable';
import { AgreedPaymentRow } from '../../../FinancialPortal/AgreedPayments/types';
import { getStudyPlansForSelect } from '../StudentList/api';
import { useStudentCurrentAccountLoader } from '../components/StudentCurrentAccountLoader';

import {
  AgreedPaymentsIndividualFilters,
  SearchControls,
} from './parts/SearchControls';

const dictPrefix = 'financialPortal.currentAccount.tabs.agreedPayments';
const ITEMS_PER_PAGE = 10;

export default function AgreedPayments(): ReactElement {
  const { t } = useTranslation();
  const { data: studentData } = useStudentCurrentAccountLoader();
  const studentId = studentData!.studentId;

  const [tableData, setTableData] = useState<AgreedPaymentRow[]>([]);
  const [filters, setFilters] = useState<AgreedPaymentsIndividualFilters>({});
  const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalPayments, setTotalPayments] = useState<number>(0);
  const [programs, setPrograms] = useState<SelectOptionType[]>([]);

  const handleSearch = useCallback(
    async ({
      searchParams,
      page,
    }: {
      searchParams: AgreedPaymentsIndividualFilters;
      page: number;
    }) => {
      setIsLoadingTable(true);
      const { data, error } = await listAgreedPaymentsByStudent(studentId, {
        ...searchParams,
        page,
        items_per_page: ITEMS_PER_PAGE,
      });
      if (error) {
        addToast({
          text: t(`${dictPrefix}.errorOnSearch`),
          color: 'danger',
          icon: 'error',
        });
      } else {
        setTableData(data?.data ?? []);
        setTotalPages(data?.total_pages || 0);
        setTotalPayments(data?.total || 0);
      }
      setIsLoadingTable(false);
    },
    [studentId, t],
  );

  const handleDownload = useCallback(async () => {
    setIsDownloadLoading(true);
    const { data, error, status } = await downloadAgreedPaymentsByStudent(
      studentId,
      filters,
    );

    if (status === 204) {
      addToast({
        text: t(`${dictPrefix}.generatingFile.empty`),
        color: 'warning',
        icon: 'warning',
      });
      setIsDownloadLoading(false);
      return;
    }

    if (error) {
      addToast({
        text: t(`${dictPrefix}.generatingFile.errorOnDownload`),
        color: 'danger',
        icon: 'error',
      });
      setIsDownloadLoading(false);
      return;
    }

    if (data) {
      addToast({
        text: t(`${dictPrefix}.generatingFile.download`),
        color: 'success',
        icon: 'success',
      });
      const blob = new Blob([data], { type: 'vnd.ms-excel' });
      downloadFromBlob(blob, `pagos_acordados_${studentId}.xls`);
    }
    setIsDownloadLoading(false);
  }, [filters, studentId, t]);

  const handleClear = useCallback(() => {
    setFilters({});
    setCurrentPage(0);
  }, []);

  useEffect(() => {
    handleSearch({ searchParams: filters, page: currentPage });
  }, [handleSearch, currentPage, filters]);

  useEffect(() => {
    let mounted = true;
    getStudyPlansForSelect().then((result) => {
      if (!mounted) {
        return;
      }
      if (result.data) {
        setPrograms(
          result.data.data.map((sp) => ({ value: sp.id, label: sp.name })),
        );
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  const hasFilters = useMemo(
    () => Boolean(filters.concept || filters.studyPlanId),
    [filters],
  );

  return (
    <Card className="p-3">
      <Row>
        <Col xs={12}>
          <SearchControls
            programs={programs}
            isDownloadLoading={isDownloadLoading}
            onSearch={(f) => {
              setCurrentPage(0);
              setFilters(f);
            }}
            onClear={handleClear}
            onDownload={handleDownload}
          />
        </Col>
        <Col xs={12}>
          <AgreedPaymentsTable
            data={tableData}
            hideStudentColumn
            totalPayments={totalPayments}
            totalPages={totalPages}
            currentPage={currentPage}
            itemsPerPage={ITEMS_PER_PAGE}
            isLoadingResults={isLoadingTable}
            hasFilters={hasFilters}
            onChangePage={(page) => setCurrentPage(page)}
          />
        </Col>
      </Row>
    </Card>
  );
}
